import api from './api';
import { revalidate } from './mutations';

export interface FilterPreset {
  id: number;
  userId: number;
  moduleName: string;
  name: string;
  description?: string;
  filters: Record<string, any>;
  isDefault: boolean;
  showInQuickFilters: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFilterPresetDto {
  moduleName: string;
  name: string;
  description?: string;
  filters: Record<string, any>;
  isDefault?: boolean;
  showInQuickFilters?: boolean;
}

export interface UpdateFilterPresetDto {
  name?: string;
  description?: string;
  filters?: Record<string, any>;
  isDefault?: boolean;
  showInQuickFilters?: boolean;
}

// Get all filter presets for the current user (optionally filtered by module)
export const getFilterPresets = async (moduleName?: string): Promise<FilterPreset[]> => {
  const params = moduleName ? `?moduleName=${encodeURIComponent(moduleName)}` : '';
  const response = await api.get(`/filter-presets${params}`);
  return response.data;
};

// Get a specific filter preset by ID
export const getFilterPreset = async (id: number): Promise<FilterPreset> => {
  const response = await api.get(`/filter-presets/${id}`);
  return response.data;
};

// Get the default filter preset for a module
export const getDefaultFilterPreset = async (moduleName: string): Promise<FilterPreset | null> => {
  try {
    const response = await api.get(`/filter-presets/default/${encodeURIComponent(moduleName)}`);
    return response.data;
  } catch (error: any) {
    // If 404, return null (no default preset exists)
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Create a new filter preset
export const createFilterPreset = async (data: CreateFilterPresetDto): Promise<FilterPreset> => {
  const response = await api.post('/filter-presets', data);
  revalidate(['filter-presets', `filter-presets-${data.moduleName}`]);
  return response.data;
};

// Update a filter preset
export const updateFilterPreset = async (id: number, data: UpdateFilterPresetDto, moduleName?: string): Promise<FilterPreset> => {
  const response = await api.patch(`/filter-presets/${id}`, data);
  // Use moduleName from response if not provided
  const presetModuleName = moduleName || response.data?.moduleName || 'all';
  revalidate(['filter-presets', `filter-presets-${presetModuleName}`]);
  return response.data;
};

// Set a filter preset as default
export const setDefaultFilterPreset = async (id: number): Promise<FilterPreset> => {
  const response = await api.patch(`/filter-presets/${id}/set-default`);
  revalidate(['filter-presets']);
  return response.data;
};

// Delete a filter preset
export const deleteFilterPreset = async (id: number, moduleName?: string): Promise<void> => {
  await api.delete(`/filter-presets/${id}`);
  revalidate(['filter-presets', moduleName ? `filter-presets-${moduleName}` : 'filter-presets']);
};

